import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { JAMB_NAV_GROUPS } from '../data/jambNav';

const G = '#047A39';

function Si({ children }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {children}
    </svg>
  );
}

const NAV_ICON = {
  '/jamb/dashboard':   <Si><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></Si>,
  '/feed':             <Si><path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4"/><polyline points="14 2 14 8 20 8"/><line x1="2" y1="12" x2="12" y2="12"/><line x1="2" y1="16" x2="8" y2="16"/></Si>,
  '/jamb/quiz':        <Si><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></Si>,
  '/jamb/exam':        <Si><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></Si>,
  '/jamb/flashcards':  <Si><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></Si>,
  '/jamb/putme':       <Si><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></Si>,
  '/jamb/cas':         <Si><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></Si>,
  '/jamb/assignments': <Si><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></Si>,
  '/jamb/results':     <Si><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></Si>,
  '/messages':         <Si><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></Si>,
  '/groups':           <Si><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Si>,
  '/notifications':    <Si><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></Si>,
  '/jamb/cutoffs':     <Si><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></Si>,
  '/jamb/finder':      <Si><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></Si>,
  '/jamb/subjects':    <Si><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></Si>,
  '/jamb/aggregate':   <Si><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="16" y2="14"/></Si>,
};

export default function JambTopbar() {
  const { currentUser, displayName, avatarInitials, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState('');

  const showPutme = currentUser?.programType === 'putme' || currentUser?.programType === 'both';
  const allow = (l) => (!l.auth || currentUser) && (!l.putme || showPutme);
  const matches = (label) => !q || label.toLowerCase().includes(q.toLowerCase());

  const groups = JAMB_NAV_GROUPS
    .map(g => g.items
      ? { ...g, items: g.items.filter(l => allow(l) && matches(l.label)) }
      : g
    )
    .filter(g => g.items ? g.items.length > 0 : (allow(g) && matches(g.label)));

  const handleLogout = () => { logout(); navigate('/jamb/signin'); };
  const isActive = (to) => pathname === to;
  const cls = (to) => `jb-sidebar__link${isActive(to) ? ' jb-sidebar__link--active' : ''}`;

  const brand = <>Toppers <span className="jb-topbar__badge">JAMB</span></>;

  const account = (close) => currentUser ? (
    <>
      <Link to="/jamb/profile" className={cls('/jamb/profile')} onClick={close}>
        <span className="jb-sidebar__link-icon"><Si><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></Si></span>
        Profile
      </Link>
      <Link to="/jamb/settings" className={cls('/jamb/settings')} onClick={close}>
        <span className="jb-sidebar__link-icon"><Si><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06-.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></Si></span>
        Settings
      </Link>
      <button className="jb-sidebar__link jb-sidebar__link--danger" onClick={handleLogout}>
        <span className="jb-sidebar__link-icon"><Si><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></Si></span>
        Sign Out
      </button>
    </>
  ) : (
    <>
      <Link to="/jamb/signin" className="jb-sidebar__link" onClick={close}>
        <span className="jb-sidebar__link-icon"><Si><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></Si></span>
        Sign In
      </Link>
      <Link to="/jamb/signup" className="jb-sidebar__link" onClick={close} style={{ color: G, fontWeight: 700 }}>
        <span className="jb-sidebar__link-icon"><Si><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></Si></span>
        Create Free Account
      </Link>
    </>
  );

  const navLinks = (close) => groups.map(g => g.items ? (
    <div key={g.label}>
      <div className="jb-sidebar__section">{g.label}</div>
      {g.items.map(l => (
        <Link key={l.to} to={l.to} className={cls(l.to)} onClick={close}>
          {NAV_ICON[l.to] && <span className="jb-sidebar__link-icon">{NAV_ICON[l.to]}</span>}
          {l.label}
        </Link>
      ))}
    </div>
  ) : (
    <Link key={g.to} to={g.to} className={cls(g.to)} onClick={close}>
      {NAV_ICON[g.to] && <span className="jb-sidebar__link-icon">{NAV_ICON[g.to]}</span>}
      {g.label}
    </Link>
  ));

  return (
    <>
      {/* ── Desktop sidebar ── */}
      <aside className="jb-sidebar">
        <Link to={currentUser ? '/jamb/dashboard' : '/jamb'} className="jb-sidebar__brand">{brand}</Link>

        {/* Search */}
        <div className="jb-sidebar__search">
          <div className="jb-sidebar__search-wrap">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="jb-sidebar__search-icon">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="jb-sidebar__search-input"
              type="text"
              placeholder="Search..."
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          </div>
        </div>

        <nav className="jb-sidebar__nav">{navLinks()}</nav>

        <div className="jb-sidebar__foot">
          {currentUser && (
            <div className="jb-sidebar__user">
              <div className="jb-avatar" style={{ width: 30, height: 30, fontSize: '0.65rem', flexShrink: 0 }}>
                {currentUser?.profilePicture ? <img src={currentUser.profilePicture} alt={displayName} /> : avatarInitials}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{currentUser?.jambTutorialId}</div>
              </div>
            </div>
          )}
          {account()}
        </div>
      </aside>

      {/* ── Mobile top bar ── */}
      <nav className="jb-topbar">
        <Link to={currentUser ? '/jamb/dashboard' : '/jamb'} className="jb-topbar__brand">{brand}</Link>
        <button className="jb-topbar__hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu" aria-expanded={menuOpen} style={{ marginLeft: 'auto' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
      </nav>

      {/* ── Mobile backdrop ── */}
      {menuOpen && <div className="jb-mobile-backdrop" onClick={() => setMenuOpen(false)} />}

      {/* ── Mobile drawer (slides from left) ── */}
      <div className={`jb-mobile-menu${menuOpen ? ' jb-mobile-menu--open' : ''}`}>
        <div className="jb-mobile-menu__header">
          <Link to={currentUser ? '/jamb/dashboard' : '/jamb'} className="jb-sidebar__brand" onClick={() => setMenuOpen(false)} style={{ padding: 0, border: 'none' }}>{brand}</Link>
          <button className="jb-topbar__hamburger" onClick={() => setMenuOpen(false)} style={{ marginLeft: 'auto', display: 'flex' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="jb-mobile-menu__nav">
          {navLinks(() => setMenuOpen(false))}
        </div>
        <div className="jb-mobile-menu__foot">
          <div style={{ height: 1, background: '#f1f5f9', margin: '8px 0' }} />
          {account(() => setMenuOpen(false))}
        </div>
      </div>
    </>
  );
}
